import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";

import { ApplicantsKeys, convertToFounder } from "../api/applicants";

interface ConvertToFounderDialogProps {
  applicant: {
    id: string;
    name: string;
    email: string;
  } | null;
  onClose: () => void;
}

const ConvertToFounderDialog: React.FC<ConvertToFounderDialogProps> = ({
  applicant,
  onClose,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutate: convert, isPending } = useMutation({
    mutationFn: (applicantId: string) => convertToFounder(applicantId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ApplicantsKeys.GET_APPLICANTS],
      });
      if (applicant) {
        queryClient.invalidateQueries({
          queryKey: [ApplicantsKeys.GET_APPLICANT, applicant.id],
        });
      }
      toast({
        title: "Success",
        description: "Applicant has been converted to a founder",
      });
      onClose();
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to convert applicant";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });

  const handleConfirm = () => {
    if (applicant) {
      convert(applicant.id);
    }
  };

  return (
    <Dialog
      open={!!applicant}
      onOpenChange={(open) => {
        if (!open && !isPending) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Convert to Founder</DialogTitle>
          <DialogDescription>
            Convert {applicant?.name} ({applicant?.email}) to a founder account.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-muted-foreground">
            This will create a founder account with Auth0, Stripe, and Circle
            access, and send welcome emails. This action cannot be undone.
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Converting...
              </>
            ) : (
              "Convert to Founder"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConvertToFounderDialog;
